import { z } from 'zod';
import { csvArray, listQuerySchema, uuid } from './common';
import { allowedHoursSchema } from './messaging';
import { AI_EFFORTS } from '../ai';
const enumOf = (values) => z.enum(values);
/** 組織設定（docs/04-data-model.md organizations.settings） */
export const orgSettingsSchema = z.object({
    limits: z.object({
        searchPerMonth: z.number().int().min(0).max(1_000_000).default(20_000),
        crawlPerDay: z.number().int().min(0).max(1_000_000).default(5_000),
        sendPerDay: z.number().int().min(0).max(100_000).default(500),
        aiCostPerMonth: z.number().min(0).max(1_000_000).default(300),
        collectPerRun: z.number().int().min(1).max(5_000).default(500),
        /** 警告閾値（%）。既定 80 */
        warningThresholdPercent: z.number().int().min(1).max(100).default(80),
    }).default({}),
    crawl: z.object({
        delaySeconds: z.number().int().min(1).max(60).default(3),
        concurrency: z.number().int().min(1).max(20).default(5),
        maxPagesPerCompany: z.number().int().min(1).max(30).default(8),
        respectRobots: z.boolean().default(true),
        recrawlIntervalDays: z.number().int().min(1).max(365).default(7),
        timeoutMs: z.number().int().min(1000).max(120_000).default(30_000),
        maxPageBytes: z.number().int().min(100_000).max(50_000_000).default(5_000_000),
    }).default({}),
    send: z.object({
        allowedHours: allowedHoursSchema.default({ daysOfWeek: [1, 2, 3, 4, 5], startHour: 9, endHour: 18 }),
        domainIntervalHours: z.number().int().min(0).max(24 * 90).default(24),
        mailPerHour: z.number().int().min(1).max(10_000).default(60),
        /** メールを HTML でも送る。開封・クリックの計測には HTML が要る */
        htmlMail: z.boolean().default(true),
        /** 本文末尾に見えない画像を入れて開封を数える */
        trackOpens: z.boolean().default(false),
        /** 本文中のリンクを計測用 URL に置き換えてクリックを数える */
        trackClicks: z.boolean().default(false),
        /** 本文末尾とヘッダに 1 クリックで配信停止できるリンクを入れる */
        unsubscribeLink: z.boolean().default(true),
    }).default({}),
    metrics: z.object({
        observationDays: z.number().int().min(1).max(180).default(14),
    }).default({}),
    ai: z.object({
        /** 使うプロバイダ。API キーは外部連携（ai_anthropic / ai_openai / ai_gemini）に登録する */
        provider: z.enum(['anthropic', 'openai', 'gemini', 'mock']).default('mock'),
        model: z.string().max(80).default('claude-opus-5'),
        /** 推論の深さ（Claude の effort ／ OpenAI の reasoning_effort ／ Gemini の thinkingLevel） */
        effort: z.enum(AI_EFFORTS).default('high'),
        /** 機能ごとにモデルを分ける（分類は軽量モデル等） */
        modelByFeature: z.record(z.string(), z.string().max(80)).default({}),
        /** モデルごとの単価（1M トークンあたりの USD） */
        pricing: z
            .record(z.string(), z.object({ inputPerMTok: z.number().min(0), outputPerMTok: z.number().min(0) }))
            .default({}),
        promptVersions: z.record(z.string(), z.string().max(60)).default({}),
    }).default({}),
    sender: z.object({
        companyName: z.string().max(120).default(''),
        personName: z.string().max(80).default(''),
        /** フォーム送信で使う担当者フリガナ・電話・郵便番号・住所（フォームに該当欄があれば差し込む） */
        personKana: z.string().max(80).default(''),
        phone: z.string().max(30).default(''),
        postalCode: z.string().max(10).default(''),
        address: z.string().max(200).default(''),
        contact: z.string().max(200).default(''),
        /** 文面末尾に必ず入れる配信停止の案内。配信停止リンクが下に付くので、そのリンクへ誘導する */
        unsubscribeNotice: z
            .string()
            .max(500)
            .default('今後のご連絡が不要な場合は、下記リンクをクリックしてください。'),
        /** 文面の {{scheduling_url}} に入る日程調整ページ */
        schedulingUrl: z
            .string()
            .url('日程調整 URL の形式が正しくありません')
            .max(500)
            .or(z.literal(''))
            .default(''),
        /** 文面の {{newsletter_url}} に入るメルマガ登録ページ */
        newsletterUrl: z
            .string()
            .url('メルマガ登録 URL の形式が正しくありません')
            .max(500)
            .or(z.literal(''))
            .default(''),
    }).default({}),
    retention: z.object({
        crawlResultsMonths: z.number().int().min(1).max(60).default(3),
        screenshotsMonths: z.number().int().min(1).max(60).default(6),
        inboundBodyMonths: z.number().int().min(1).max(60).default(12),
        aiPayloadMonths: z.number().int().min(1).max(60).default(3),
        auditLogsMonths: z.number().int().min(1).max(120).default(12),
    }).default({}),
    collection: z.object({
        /** 共通の除外ドメイン・キーワード */
        excludeDomains: z.array(z.string().max(253)).max(2000).default([]),
        excludeKeywords: z.array(z.string().max(80)).max(200).default([]),
    }).default({}),
});
/**
 * 部分更新。上の階層の項目だけを任意にする（渡した項目の中は、足りない所が既定値で埋まる）。
 * 以前の deepPartial() と同じ結果（中の項目は .default({}) なので深くは効いていなかった）。deepPartial は zod 4 で無くなる（#143）
 */
export const updateOrgSettingsSchema = orgSettingsSchema.partial();
export const INTEGRATION_PROVIDERS = [
    'brave',
    'ai_anthropic',
    'ai_openai',
    'ai_gemini',
    'mail_send',
    'mail_receive',
    'storage',
    'timerex',
    'google_workspace',
];
export const INTEGRATION_LABELS = {
    brave: 'Brave Search',
    ai_anthropic: 'AI（Claude）',
    ai_openai: 'AI（OpenAI）',
    ai_gemini: 'AI（Gemini）',
    mail_send: 'メール送信',
    mail_receive: 'メール受信',
    storage: 'ストレージ',
    timerex: '日程調整（TimeRex）',
    google_workspace: 'Google Workspace（メーラー）',
};
/**
 * TimeRex 連携の設定（integration_settings.config）。API キーは encrypted_key に入れる。
 * 文面に差し込むワンタイム URL は calendarId のカレンダーで発行する。
 */
export const timerexConfigSchema = z.object({
    teamId: z.string().max(80).default(''),
    teamName: z.string().max(120).default(''),
    calendarId: z.string().max(80).default(''),
    calendarName: z.string().max(120).default(''),
    calendarUrl: z.string().max(500).default(''),
});
/** 連携先のカレンダー（選択肢として画面に出す） */
export const timerexCalendarSchema = z.object({
    id: z.string(),
    name: z.string(),
    folderName: z.string().nullable(),
    url: z.string(),
    duration: z.number(),
    onlineMeetingProvider: z.string().nullable(),
});
export const SchedulingLinkStatuses = ['pending', 'confirmed', 'cancelled'];
export const SCHEDULING_LINK_STATUS_LABELS = {
    pending: '予約待ち',
    confirmed: '予約済み',
    cancelled: 'キャンセル',
};
/** 企業ごとの日程調整リンク（画面向け） */
export const schedulingLinkSchema = z.object({
    id: uuid,
    companyId: uuid,
    campaignId: uuid.nullable(),
    appointmentId: uuid.nullable(),
    provider: z.string(),
    url: z.string(),
    status: z.enum(SchedulingLinkStatuses),
    scheduledAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    guestName: z.string().nullable(),
    guestEmail: z.string().nullable(),
    meetingUrl: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** AI プロバイダ名 → API キーを持つ連携 */
export const AI_INTEGRATION_BY_PROVIDER = {
    anthropic: 'ai_anthropic',
    openai: 'ai_openai',
    gemini: 'ai_gemini',
};
/** メール送信の実装。resend は API キーだけで送れる */
export const MAIL_DRIVERS = ['resend', 'smtp', 'log'];
export const MAIL_DRIVER_LABELS = {
    resend: 'Resend（API キー）',
    smtp: 'SMTP サーバー',
    log: '送信しない（記録のみ）',
};
/**
 * メール送信連携の設定（integration_settings.config）。
 * API キーは config ではなく encrypted_key に入れる。
 */
export const mailSendConfigSchema = z.object({
    driver: z.enum(MAIL_DRIVERS).default('resend'),
    /** 差出人。Resend では検証済みドメインのアドレスにする */
    fromAddress: z.string().email('差出人アドレスの形式が正しくありません').or(z.literal('')).default(''),
    fromName: z.string().max(80).default(''),
    /** 返信先。空なら差出人と同じ */
    replyTo: z.string().email('返信先アドレスの形式が正しくありません').or(z.literal('')).default(''),
    smtpHost: z.string().max(253).default(''),
    smtpPort: z.number().int().min(1, 'ポート番号を確認してください').max(65535, 'ポート番号を確認してください').default(587),
    smtpSecure: z.boolean().default(false),
    smtpUser: z.string().max(200).default(''),
    /** 保存時に暗号化する。読み出しでは返さない */
    smtpPassword: z.string().max(500).optional(),
});
/** メール受信の認証方式。Google Workspace はパスワードでの IMAP サインインを終了しているため、サービスアカウントの OAuth を使う */
export const MAIL_RECEIVE_AUTHS = ['google_oauth', 'password'];
export const MAIL_RECEIVE_AUTH_LABELS = {
    google_oauth: 'Google Workspace（サービスアカウント）',
    password: 'IMAP（ユーザー名とパスワード）',
};
/**
 * メール受信連携の設定（integration_settings.config）。
 * サービスアカウントの鍵は config ではなく encrypted_key に入れ、masked_key にはその client_email を出す。
 */
export const mailReceiveConfigSchema = z.object({
    auth: z.enum(MAIL_RECEIVE_AUTHS).default('google_oauth'),
    /** 読み取る受信箱のアドレス。google_oauth では委任先のユーザーになる */
    user: z.string().email('受信箱のアドレスの形式が正しくありません').or(z.literal('')).default(''),
    /** 接続先。google_oauth では省略でき、imap.gmail.com になる */
    host: z.string().max(253).default(''),
    port: z.number().int().min(1, 'ポート番号を確認してください').max(65535, 'ポート番号を確認してください').default(993),
    mailbox: z.string().max(200).default('INBOX'),
    /** password のとき。保存時に暗号化する。読み出しでは返さない */
    imapPassword: z.string().max(500).optional(),
    /** この日以降に届いたメールから取り込む。空なら、設定した時点より後に届くメールだけ */
    since: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '取り込み開始日は YYYY-MM-DD で指定してください')
        .or(z.literal(''))
        .default(''),
});
export const integrationStatusSchema = z.object({
    provider: enumOf(INTEGRATION_PROVIDERS),
    label: z.string(),
    configured: z.boolean(),
    /** マスク表示（例: bs-...abcd）。平文は返さない */
    maskedKey: z.string().nullable(),
    config: z.record(z.string(), z.unknown()),
    lastTestedAt: z.string().nullable(),
    lastTestResult: z.enum(['success', 'failed']).nullable(),
    lastTestMessage: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const putIntegrationSchema = z.object({
    /** API キー。メール受信ではサービスアカウントの鍵（JSON）が入るため長めに取る */
    apiKey: z.string().max(10_000).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
});
export const quotaSchema = z.object({
    aiCost: z.object({
        limitUsd: z.number().nullable(),
        usedUsd: z.number(),
        level: z.enum(['ok', 'warning', 'blocked']),
    }),
    search: z.object({
        limitCalls: z.number().nullable(),
        usedCalls: z.number(),
        level: z.enum(['ok', 'warning', 'blocked']),
    }),
    send: z.object({
        dailyLimit: z.number().nullable(),
        usedToday: z.number(),
        level: z.enum(['ok', 'warning', 'blocked']),
    }),
    crawl: z.object({
        dailyLimit: z.number().nullable(),
        usedToday: z.number(),
        level: z.enum(['ok', 'warning', 'blocked']),
    }),
});
// ---------------------------------------------------------------- logs / jobs / usage
export const jobRunSchema = z.object({
    id: uuid,
    queue: z.string(),
    jobName: z.string(),
    jobLabel: z.string(),
    jobId: z.string().nullable(),
    status: z.string(),
    targetLabel: z.string().nullable(),
    progressTotal: z.number().int(),
    progressDone: z.number().int(),
    errorCount: z.number().int(),
    resultSummary: z.record(z.string(), z.unknown()).nullable(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    updatedAt: z.string(),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
    canCancel: z.boolean(),
    /** 30 分以上更新がない場合 true */
    isStalled: z.boolean(),
    href: z.string().nullable(),
});
export const jobsQuerySchema = listQuerySchema.extend({
    status: csvArray(z.string()),
    queue: csvArray(z.string()),
    mineOnly: z.union([z.boolean(), z.string()]).optional(),
});
export const auditLogSchema = z.object({
    id: uuid,
    actor: z.object({ id: uuid, name: z.string().nullable(), role: z.string() }).nullable(),
    action: z.string(),
    actionLabel: z.string(),
    targetType: z.string().nullable(),
    targetId: uuid.nullable(),
    summary: z.string().nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    traceId: z.string().nullable(),
    createdAt: z.string(),
});
export const auditLogsQuerySchema = listQuerySchema.extend({
    actorId: uuid.optional(),
    action: csvArray(z.string()),
    targetType: z.string().max(40).optional(),
    targetId: uuid.optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const executionLogsQuerySchema = listQuerySchema.extend({
    kind: z.enum(['collection', 'crawl', 'send', 'ai']).default('send'),
    from: z.string().optional(),
    to: z.string().optional(),
    result: csvArray(z.string()),
});
export const costQuerySchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    groupBy: z.enum(['provider', 'feature', 'user', 'campaign', 'date']).default('provider'),
});
export const costRowSchema = z.object({
    key: z.string(),
    label: z.string(),
    callCount: z.number().int(),
    unitCount: z.number().int(),
    costUsd: z.number(),
});
export const notificationSchema = z.object({
    id: uuid,
    kind: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    href: z.string().nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
export const exportJobSchema = z.object({
    id: uuid,
    status: z.string(),
    fileName: z.string().nullable(),
    rowCount: z.number().int().nullable(),
    downloadUrl: z.string().nullable(),
    expiresAt: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=settings.js.map